import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Users, Plus, UserCheck, UserX, Trash2, Loader2, KeyRound, Copy, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusTone = (s) =>
  s === "active" ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600";

export const CleanersPanel = () => {
  const [cleaners, setCleaners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", phone: "" });
  const [inviting, setInviting] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);

  const fetchCleaners = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/cleaners`);
      setCleaners(Array.isArray(data) ? data : []);
    } catch (err) {
      logError("Failed to load cleaners", err);
      toast.error("Could not load cleaner accounts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCleaners(); }, []);

  const submitInvite = async () => {
    if (!inviteForm.name.trim() || !inviteForm.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setInviting(true);
    try {
      const { data } = await axios.post(`${API}/admin/cleaners`, inviteForm);
      setTempPassword({ email: data.email, password: data.temp_password });
      setInviteForm({ name: "", email: "", phone: "" });
      fetchCleaners();
      toast.success("Cleaner account created");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not create account";
      toast.error(typeof detail === "string" ? detail : "Could not create account");
    } finally {
      setInviting(false);
    }
  };

  const toggleStatus = async (cleaner) => {
    const next = cleaner.status === "active" ? "disabled" : "active";
    try {
      await axios.patch(`${API}/admin/cleaners/${cleaner.id}`, { status: next });
      toast.success(`Account ${next}`);
      fetchCleaners();
    } catch {
      toast.error("Update failed");
    }
  };

  const removeCleaner = async (cleaner) => {
    if (!window.confirm(`Delete ${cleaner.name}'s account? They will lose access immediately.`)) return;
    try {
      await axios.delete(`${API}/admin/cleaners/${cleaner.id}`);
      toast.success("Account deleted");
      fetchCleaners();
    } catch {
      toast.error("Delete failed");
    }
  };

  const copyPassword = async () => {
    if (!tempPassword) return;
    try {
      await navigator.clipboard.writeText(tempPassword.password);
      toast.success("Password copied");
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="cleaners-loading">
        <Loader2 className="w-6 h-6 animate-spin text-[#2D5016]" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="admin-cleaners-panel"
    >
      <Card className="bg-white border-[#E2E8E0]">
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <CardTitle className="text-[#1A2E12] flex items-center gap-2">
              <Users className="w-5 h-5 text-[#2D5016]" />
              Cleaner Accounts ({cleaners.length})
            </CardTitle>
            <Dialog open={inviteOpen} onOpenChange={(open) => {
              setInviteOpen(open);
              if (!open) setTempPassword(null);
            }}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
                  data-testid="invite-cleaner-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Invite cleaner
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="invite-cleaner-modal">
                <DialogHeader>
                  <DialogTitle>Invite a new cleaner</DialogTitle>
                </DialogHeader>

                {tempPassword ? (
                  <div className="space-y-4 py-2">
                    <div className="flex items-start gap-3 bg-[#E8F5E9] border border-[#7CB342]/30 rounded-xl p-4">
                      <CheckCircle2 className="w-5 h-5 text-[#2D5016] mt-0.5 shrink-0" />
                      <div className="flex-1 text-sm">
                        <p className="font-semibold text-[#1A2E12] mb-1">Account created</p>
                        <p className="text-[#5C6B57]">
                          Share these credentials with <strong>{tempPassword.email}</strong>. They&apos;ll be
                          asked to set a new password on first sign-in.
                        </p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs text-[#5C6B57]">Temporary password</Label>
                      <div className="flex gap-2">
                        <Input
                          value={tempPassword.password}
                          readOnly
                          className="font-mono text-sm"
                          data-testid="temp-password-value"
                        />
                        <Button
                          variant="outline"
                          onClick={copyPassword}
                          data-testid="copy-temp-password"
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-[11px] text-[#A23B3B]">
                        This is the only time you&apos;ll see it — copy it now.
                      </p>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setTempPassword(null)}
                        data-testid="invite-another"
                      >
                        Invite another
                      </Button>
                      <Button
                        className="bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                        onClick={() => {
                          setTempPassword(null);
                          setInviteOpen(false);
                        }}
                        data-testid="close-invite-modal"
                      >
                        Done
                      </Button>
                    </DialogFooter>
                  </div>
                ) : (
                  <div className="space-y-3 py-2">
                    <div>
                      <Label className="text-xs text-[#5C6B57]">Full name *</Label>
                      <Input
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm((p) => ({ ...p, name: e.target.value }))}
                        placeholder="e.g. Amina Okonkwo"
                        className="mt-1"
                        data-testid="invite-name"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6B57]">Email *</Label>
                      <Input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm((p) => ({ ...p, email: e.target.value }))}
                        placeholder="cleaner@example.com"
                        className="mt-1"
                        data-testid="invite-email"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-[#5C6B57]">Phone (optional)</Label>
                      <Input
                        value={inviteForm.phone}
                        onChange={(e) => setInviteForm((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="+44 ..."
                        className="mt-1"
                        data-testid="invite-phone"
                      />
                    </div>
                    <DialogFooter>
                      <Button
                        variant="outline"
                        onClick={() => setInviteOpen(false)}
                        data-testid="cancel-invite"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={submitInvite}
                        disabled={inviting}
                        className="bg-[#2D5016] hover:bg-[#1A2E12] text-white"
                        data-testid="submit-invite"
                      >
                        {inviting ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating…</>
                        ) : (
                          <>Create account</>
                        )}
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>

        <CardContent>
          {cleaners.length === 0 ? (
            <div className="text-center py-16 text-[#5C6B57]" data-testid="cleaners-empty">
              <KeyRound className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No cleaner accounts yet.</p>
              <p className="text-xs mt-1">
                Meanwhile the shared code <code className="bg-[#F9FAF9] px-1.5 py-0.5 rounded">CLEANKIN2024</code> continues to work.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Last login</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cleaners.map((c) => (
                    <TableRow key={c.id} data-testid={`cleaner-row-${c.id}`}>
                      <TableCell className="font-medium">{c.name}</TableCell>
                      <TableCell className="text-sm text-[#5C6B57]">{c.email}</TableCell>
                      <TableCell className="text-sm text-[#5C6B57]">{c.phone || "—"}</TableCell>
                      <TableCell>
                        <Badge className={statusTone(c.status)}>
                          {c.status}
                          {c.must_change_password && c.status === "active" && (
                            <span className="ml-1 opacity-70">· temp pw</span>
                          )}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-[#5C6B57]">
                        {c.last_login_at
                          ? new Date(c.last_login_at).toLocaleString("en-GB", {
                              day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                            })
                          : "Never"}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleStatus(c)}
                            data-testid={`toggle-cleaner-${c.id}`}
                          >
                            {c.status === "active" ? (
                              <><UserX className="w-4 h-4 mr-1" /> Disable</>
                            ) : (
                              <><UserCheck className="w-4 h-4 mr-1" /> Enable</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => removeCleaner(c)}
                            data-testid={`delete-cleaner-${c.id}`}
                          >
                            <Trash2 className="w-4 h-4 text-[#A23B3B]" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default CleanersPanel;
